// La grilla del mes: qué día va en cada casillero y qué posts caen ahí.
//
// Vive afuera del componente porque es aritmética de fechas, que es donde se
// esconden los errores que sólo aparecen en marzo o en un año bisiesto. Acá se
// puede probar sin montar nada.

import type { PostDeRedes } from "./post";

/** Lo mínimo para ubicar algo en el calendario. */
export interface Agendable {
  cuando: Date | null;
}

/**
 * Genérico sobre el post: la pantalla trabaja con posts que además traen sus
 * miniaturas firmadas, y fijar el tipo acá las perdía en el camino.
 */
export interface DiaDelMes<T extends Agendable = PostDeRedes> {
  fecha: Date;
  /** Pertenece al mes que se está mirando (los bordes son del mes vecino). */
  delMes: boolean;
  esHoy: boolean;
  /** Ya pasó: no se puede agendar nada acá. */
  esPasado: boolean;
  posts: T[];
}

/**
 * La semana arranca el LUNES.
 *
 * El calendario viejo del Piloto Automático arranca el domingo y está rotulado
 * en inglés, que es de cuando esa pantalla no se tradujo. Acá los usuarios son
 * argentinos y la semana laboral empieza el lunes: el fin de semana se lee
 * junto, al final, que es como se piensa una agenda de publicaciones.
 */
export const DIAS_DE_LA_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/** Cuántas casillas antes del día 1. Lunes = 0 … domingo = 6. */
function corrimientoDelPrimero(mes: Date): number {
  const primero = new Date(mes.getFullYear(), mes.getMonth(), 1);
  return (primero.getDay() + 6) % 7;
}

function mismoDia(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Seis semanas SIEMPRE, aunque el mes entre en cinco.
 *
 * Con filas variables el calendario cambia de alto al pasar de mes y todo lo
 * que está abajo salta. Una fila de más vacía cuesta menos que eso.
 */
export const SEMANAS = 6;

export function grillaDelMes<T extends Agendable>(
  mes: Date,
  posts: readonly T[],
  ahora: Date = new Date(),
): Array<DiaDelMes<T>> {
  const arranque = new Date(
    mes.getFullYear(),
    mes.getMonth(),
    1 - corrimientoDelPrimero(mes),
  );
  const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());

  return Array.from({ length: SEMANAS * 7 }, (_, i) => {
    const fecha = new Date(arranque.getFullYear(), arranque.getMonth(), arranque.getDate() + i);
    return {
      fecha,
      delMes: fecha.getMonth() === mes.getMonth() && fecha.getFullYear() === mes.getFullYear(),
      esHoy: mismoDia(fecha, hoy),
      esPasado: fecha.getTime() < hoy.getTime(),
      posts: posts
        .filter((p) => p.cuando && mismoDia(p.cuando, fecha))
        .sort((a, b) => a.cuando!.getTime() - b.cuando!.getTime()),
    };
  });
}

/** "septiembre 2026" */
export function tituloDelMes(mes: Date): string {
  return `${MESES[mes.getMonth()]} ${mes.getFullYear()}`;
}

/**
 * Correr el mes. Siempre desde el día 1: sumarle un mes a un 31 cae en el mes
 * siguiente al que se quería (el 31 de marzo + 1 mes da 1 de mayo).
 */
export function correrMes(mes: Date, cuantos: number): Date {
  return new Date(mes.getFullYear(), mes.getMonth() + cuantos, 1);
}

/** El mes al que pertenece una fecha, normalizado al día 1. */
export function mesDe(fecha: Date): Date {
  return new Date(fecha.getFullYear(), fecha.getMonth(), 1);
}

/** "12:00" — la hora del post en la casilla del día. */
export function horaDe(p: Agendable): string {
  if (!p.cuando) return "";
  return `${String(p.cuando.getHours()).padStart(2, "0")}:${String(p.cuando.getMinutes()).padStart(2, "0")}`;
}
